import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { HTTPError, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { getProxyConfigPayload } from "common/helpers/utils";
import type { LinkedInProfile, WorkspaceAccount } from "common/types";

import useWorkspaceCacheUpdate from "./useWorkspaceCacheUpdate";

async function addLinkedInAccountToWorkspaceRequest(
  workspaceId: string,
  accountId: string,
): Promise<WorkspaceAccount[]> {
  return (
    await post(`workspaces/${workspaceId}/accounts`, {
      account_id: accountId,
    })
  ).accounts;
}

async function getAccountFromCookiesRequest(
  workspaceId: string,
  cookies: object[],
  selectedProxy: string,
): Promise<LinkedInProfile> {
  const { profile } = await post(
    `workspaces/${workspaceId}/accounts-login-with-cookies`,
    {
      cookies,
      proxy_config: getProxyConfigPayload(selectedProxy),
    },
  );

  //this endpoint returns id as number, convert to string to match the type
  return { ...profile, id: profile.id.toString() };
}

export default function useAimfoxExtensionMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { updateCachedWorkspace, updateCachedWorkspaceAccounts } =
    useWorkspaceCacheUpdate();

  const {
    mutateAsync: addLinkedInAccountToWorkspace,
    isPending: isAddingLinkedInAccountToWorkspace,
  } = useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      addLinkedInAccountToWorkspaceRequest(workspaceId, accountId),
    onSuccess: (workspaceAccounts, { accountId }) => {
      updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
        const existingAccount = draftAccounts.find(
          ({ id }) => id === accountId,
        );

        // Reconnect case
        if (existingAccount) {
          existingAccount.state = "LoggedIn";
        } else {
          const newAccount = workspaceAccounts.find(
            ({ id }) => id === accountId,
          );

          draftAccounts.push(newAccount);
        }
      });

      updateCachedWorkspace(workspaceId, (draftWorkspace) => {
        draftWorkspace.accounts_count += 1;
      });

      toast.success("Account connected");
    },
    onError: (error) => {
      if (error instanceof HTTPError && error.code === 409) {
        toast.error(
          "This account is already in use and cannot be connected again",
        );
      } else {
        toast.error(
          "Failed to connect. Please make sure you are logged into your LinkedIn account",
        );
      }
    },
  });

  const {
    mutateAsync: getAccountFromCookies,
    isPending: isGettingAccount,
    error: errorGettingAccountFromCookies,
  } = useMutation({
    mutationFn: ({
      cookies,
      selectedProxy,
    }: {
      cookies: object[];
      selectedProxy: string;
    }) => getAccountFromCookiesRequest(workspaceId, cookies, selectedProxy),
    onError: (error) => {
      Sentry.setExtra("error", error);
      Sentry.setExtra("error_json", JSON.stringify(error));
      Sentry.captureMessage("Failed to connect with Aimfox Chrome Extension");
    },
  });

  return {
    addLinkedInAccountToWorkspace,
    isAddingLinkedInAccountToWorkspace,
    getAccountFromCookies,
    isGettingAccount,
    errorGettingAccountFromCookies,
  };
}
